import { findOptionByName } from './optionNameMatch';

/**
 * Prefills the HRIS "Job Profile" select from the Kula job's linked job
 * profile, by name.
 *
 * Rather than writing into `initialValues` directly, this seeds the field's
 * `default_value` on the raw Kombo schema before it is converted. That reuses
 * the two mechanisms already built around `default_value`:
 *
 *  - `seedDefaultValueOptions` (formConfigUtils) injects the chosen option into
 *    the select's option list. This matters — `createSelectConfig` only maps
 *    the first 500 entries, and a Workday Job Profile list runs well past that,
 *    so a bare id would leave the field looking empty.
 *  - `extractDefaultValuesFromSchema` (useHrisIntegrations) lifts it into the
 *    form's initial values.
 *
 * A `default_value` already supplied by Kombo always wins — that one came from
 * the HRIS itself and is better evidence than a name match.
 */

interface SchemaField {
  type?: string;
  required?: boolean;
  default_value?: unknown;
  properties?: Record<string, SchemaField>;
  options?: { entries?: { id?: string; label?: string; name?: string }[] };
  [key: string]: unknown;
}

type Schema = Record<string, SchemaField>;

/**
 * Kombo names this field per HRIS — `Job_Profile_Reference` on Workday, plainer
 * elsewhere — so the key is matched on shape rather than an exact string.
 */
const JOB_PROFILE_KEY = /job[\s_-]?profile/i;

const isJobProfileField = (key: string, field: SchemaField): boolean =>
  JOB_PROFILE_KEY.test(key) &&
  (field?.type === 'single_select' || field?.type === 'multi_select');

const hasOwnDefault = (field: SchemaField): boolean =>
  field.default_value !== undefined && field.default_value !== null;

/**
 * True when the form has a job profile select still waiting for a value.
 *
 * Callers use this to decide whether looking the name up is worth it at all —
 * most HRIS forms have no such field, and Kombo sometimes supplies its own
 * default, so gating on this keeps a network round trip off the form-open path.
 */
export const needsJobProfilePrefill = (schema: Schema | undefined): boolean => {
  if (!schema) return false;

  return Object.entries(schema).some(([key, field]) => {
    if (!field || typeof field !== 'object') return false;
    if (field.type === 'object' && field.properties)
      return needsJobProfilePrefill(field.properties);
    return isJobProfileField(key, field) && !hasOwnDefault(field);
  });
};

/**
 * Returns a copy of `schema` with the job profile field's `default_value` set
 * to the id of the option matching `jobProfileName`.
 *
 * The schema is returned untouched when there is no name, no job profile field,
 * no confident match, or the field already carries a default. Only the branches
 * on the path to a changed field are cloned; everything else is shared.
 */
export const withJobProfileDefault = (
  schema: Schema | undefined,
  jobProfileName: string | undefined | null
): Schema | undefined => {
  if (!schema || !jobProfileName?.trim()) return schema;

  // `changed` is per-call rather than shared across the recursion: a match in
  // one branch must not force untouched sibling branches to be cloned too
  const decorate = (node: Schema): Schema => {
    const next: Schema = {};
    let touched = false;

    for (const [key, field] of Object.entries(node)) {
      next[key] = field;

      if (!field || typeof field !== 'object') continue;

      if (field.type === 'object' && field.properties) {
        const properties = decorate(field.properties);
        if (properties !== field.properties) {
          next[key] = { ...field, properties };
          touched = true;
        }
        continue;
      }

      if (isJobProfileField(key, field) && !hasOwnDefault(field)) {
        const match = findOptionByName(field.options?.entries, jobProfileName);
        if (match?.id) {
          next[key] = { ...field, default_value: match.id };
          touched = true;
        }
      }
    }

    return touched ? next : node;
  };

  return decorate(schema);
};

interface ApplicationLike {
  job_id?: string | number;
  ats_job?: { id?: string | number; job_profile?: JobProfile | null } | null;
}

interface JobProfile {
  name?: string;
  job_title?: string;
}

/** A profile's `name` is the field shown in the UI; `job_title` is the fallback. */
const profileName = (profile: JobProfile | null | undefined) =>
  profile?.name || profile?.job_title || undefined;

/**
 * Reads the job profile name already present on the application, if any.
 *
 * Today the embedded `ats_job` is a trimmed serialization — id, name, status,
 * offices, permissions — with no `job_profile`, so this normally returns
 * undefined and the caller has to fetch. It is checked first so that the extra
 * request disappears on its own if the payload ever starts including it.
 */
export const getJobProfileName = (
  application: ApplicationLike | undefined
): string | undefined => profileName(application?.ats_job?.job_profile);

/**
 * Resolves the job profile name, fetching the job when the application does not
 * already carry it.
 *
 * Returns undefined rather than throwing on a failed fetch: this only powers a
 * prefill, and the form must open regardless.
 */
export const resolveJobProfileName = async (
  application: ApplicationLike | undefined,
  fetchJob: (jobId: string | number) => Promise<{ job_profile?: JobProfile }>
): Promise<string | undefined> => {
  const embedded = getJobProfileName(application);
  if (embedded) return embedded;

  const jobId = application?.job_id ?? application?.ats_job?.id;
  if (!jobId || typeof fetchJob !== 'function') return undefined;

  try {
    const job = await fetchJob(jobId);
    return profileName(job?.job_profile);
  } catch {
    return undefined;
  }
};
